import React, { useEffect, useState } from "react";
import { Flex, Table, TableColumnsType, message } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { IPageData } from "../../../../shared/dto/PageData";
import HeaderTitle from "./config/HeaderTitle";
import { IUsuarioSimplificado } from "../../../../shared/dto/UsuarioSimplificado";
import SearchInput from "../SearchInput";
import UserProvider from "../../../../shared/provider/UserProvider";
import EditarUsuario from "../EditarUsuario";
import getLetterColor from "../../../../shared/util/GetLetterColor";
import AdicionarUsuario from "../AdicionarUsuario";
import { FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa";
import sortList from "../../../../shared/util/Order";
import { UserListImage } from "./styles";

function UserList({
	openAddUser,
	setOpenAddUser,
}: {
	openAddUser: boolean;
	setOpenAddUser: React.Dispatch<boolean>;
}): JSX.Element {
	const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">();
	const [sortKey, setSortKey] = React.useState<string>();
	const [editUser, setEditUser] = React.useState(false);
	const [selectedUserForEdit, setSelectedUserForEdit] = React.useState(
		{} as IUsuarioSimplificado
	);

	const [response, setResponse] = useState<
		IPageData<Array<IUsuarioSimplificado>>
	>({} as IPageData<Array<IUsuarioSimplificado>>);
	const [list, setList] = useState<IUsuarioSimplificado[]>([]);

	const userProvider = new UserProvider();
	const navigate = useNavigate();

	function getPreviousSelectedUserForEdit(data: IUsuarioSimplificado[]) {
		if (!data) return;
		const user = data.find((user) => user.id === selectedUserForEdit.id);
		if (user) setSelectedUserForEdit(user);
	}

	async function requireUserList(
		page = 1,
		itemsPerPage = 10,
		orderBy?: string,
		direction?: "asc" | "desc"
	) {
		try {
			const data = await userProvider.getAllUsers({
				page,
				itemsPerPage,
				orderBy,
				direction,
			});
			if (!data) return;

			setResponse(data);
			getPreviousSelectedUserForEdit(data.data);
		} catch (error) {
			message.error(
				<span>
					<b>Sessão expirada.</b> Você será redirecionado!
				</span>,
			);
			setTimeout(() => navigate("/entrar"), 500);
		}
	}

	useEffect(() => {
		requireUserList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleEditUserClick(user: IUsuarioSimplificado) {
		setSelectedUserForEdit(user);
		setEditUser(true);
	}

	function handleSortList() {
		if (sortKey) {
			const direction = sortDirection === "asc" ? "desc" : "asc";
			const sorted = sortList(
				response.data,
				sortKey as keyof IUsuarioSimplificado,
				direction
			);
			setList(sorted);
			setResponse({ ...response, data: sorted });
		}
	}

	React.useEffect(() => {
		handleSortList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortDirection, sortKey]);

	const columns: TableColumnsType<IUsuarioSimplificado> = [
		{
			title: 'Nome',
			dataIndex: 'name',
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (name: string, user: IUsuarioSimplificado) => (
				<Flex align="center">
					<UserListImage
						letterColor={
							user && user.name
								? getLetterColor(user.name.charAt(0))
								: ""
						}
						image={
							user.profileImage
								? "data:image/png;base64," + user.profileImage	: ""
						}
					>
						{!user.profileImage &&
							user.name &&
							user.name.length > 0 &&
							user.name.charAt(0)}
					</UserListImage>
					<span>{name}</span>
				</Flex>
			),
		},
		{
			title: 'Email',
			dataIndex: 'email',
			sorter: true,
		},
		{
			title: 'Tenant',
			dataIndex: 'tenantName',
			sorter: true,
		},
		{
			title: 'Criado em',
			dataIndex: 'createdAt',
			sorter: true,
			render: (createdAt: string) => moment(createdAt).format("DD/MM/YYYY"),
		},
		{
			title: 'Perfil',
			dataIndex: 'roleName',
			sorter: true,
		},
		{
			title: 'Ações',
			dataIndex: 'actions',
			render: (_: any, user: IUsuarioSimplificado) => (
				<span
					className="user-list-editar-button"
					onClick={() => handleEditUserClick(user)}
				>
					editar
				</span>
			),
		},
	];

	return (
		<div>
			<AdicionarUsuario
				open={openAddUser}
				setOpen={setOpenAddUser}
				requireUserList={requireUserList}
			/>
			<EditarUsuario
				open={editUser}
				setOpen={setEditUser}
				user={selectedUserForEdit}
				requireUserList={requireUserList}
			/>

			<div style={{ marginBottom: 16 }}>
				<SearchInput setListPaginated={setResponse} />
				{response && <span>{response.totalItems} usuários cadastrados</span>}
			</div>

			<Table<IUsuarioSimplificado>
				columns={columns}
				dataSource={response.data || []}
				pagination={{
					total: response.totalItems,
					pageSize: response.itemsPerPage,
					current: response.currentPage,
					onChange: (page, pageSize) => requireUserList(page, pageSize),
				}}
				rowKey="id"
			/>
		</div>
	);
}

export default UserList;
